import React from "react";
import { Github, Linkedin, Mail, ArrowDown , Code2} from "lucide-react";
import Typewriter from "./Typewriter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const trackResumeView = () => {
  try {
    const url = `${BACKEND_URL}/api/track-resume-view`;
    const body = JSON.stringify({
      source: "hero-download-resume-btn",
      referrer: document.referrer || null,
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {
    // never block the download on a tracking failure
  }
};

const SOCIALS = [
  {
    id: "github",
    label: "GitHub",
    href: "https://github.com/srivastava-aryan",
    Icon: Github,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/aryan-srivastava2608",
    Icon: Linkedin,
  },
  {
    id: "email",
    label: "Email",
    href: "mailto:aryansrivastava0826@gmail.com",
    Icon: Mail,
  },
  // {
  //   id: "instagram",
  //   label: "Instagram",
  //   href: "https://www.instagram.com/aryan.srivastava2608/",
  //   Icon: Instagram,
  // },
  {
    id: "leetcode",
    label: "LeetCode",
    href: "https://leetcode.com/aryan.srivastava2608/",
    Icon: Code2,
  },
];

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-line"
    >
      <div className="mx-auto grid max-w-6xl gap-10 px-6 pb-24 pt-32 md:grid-cols-12 md:gap-16 md:pb-32 md:pt-44">
        {/* Left rail: meta */}
        <div className="md:col-span-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-dim">
            <p>./aryan_srivastava</p>
            <p className="mt-1">v1.0.0</p>
          </div>
          <div className="mt-8 hidden h-40 w-px bg-gradient-to-b from-accent/40 via-line to-transparent md:block" />
        </div>

        {/* Main column */}
        <div className="md:col-span-9">
          {/* Status pill */}
          <div
            data-testid="hero-status"
            className="inline-flex items-center gap-3 rounded-sm border border-line bg-bg-surface/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-ink-secondary backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            <span className="text-ink-primary">STATUS</span>
            <span className="text-ink-dim">::</span>
            <span>Open to full-time roles — Available.</span>
          </div>

          {/* Name */}
          <h1
            data-testid="hero-name"
            className="mt-8 font-mono text-[44px] font-semibold leading-[1.05] tracking-tight text-ink-primary sm:text-6xl md:text-7xl"
          >
            Aryan
            <br />
            Srivastava<span className="text-accent">.</span>
          </h1>

          {/* Tagline with typewriter */}
          <div
            data-testid="hero-tagline"
            className="mt-6 font-mono text-lg tracking-tight text-ink-secondary sm:text-xl md:text-2xl"
          >
            <span className="text-accent">$</span>{" "}
            <Typewriter
              text="AI Engineer // Full-Stack Developer"
              testId="hero-typewriter"
            />
          </div>

          {/* Body */}
          <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-ink-secondary md:text-lg">
            I build production AI systems and the full-stack infrastructure they
            run on — retrieval-augmented pipelines, agentic backends, and the
            careful frontends that make them feel inevitable.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#projects"
              data-testid="hero-view-projects-btn"
              className="btn-terminal group inline-flex items-center gap-2 border border-accent/60 bg-transparent px-5 py-3 font-mono text-sm uppercase tracking-widest text-accent"
            >
              <span className="text-accent/70">[</span>
              <span>VIEW_PROJECTS</span>
              <span className="text-accent/70">]</span>
              <ArrowDown
                size={14}
                className="ml-1 transition-transform group-hover:translate-y-0.5"
              />
            </a>
            <a
              href="/myresume.pdf"
              data-testid="hero-download-resume-btn"
              download
              onClick={trackResumeView}
              className="btn-terminal inline-flex items-center gap-2 border border-line bg-bg-surface/60 px-5 py-3 font-mono text-sm uppercase tracking-widest text-ink-primary"
            >
              <span className="text-ink-dim">[</span>
              <span>DOWNLOAD_RESUME</span>
              <span className="text-ink-dim">]</span>
            </a>
          </div>

          {/* Socials */}
          <ul
            data-testid="hero-socials"
            className="mt-10 flex items-center gap-2 font-mono text-xs text-ink-secondary"
          >
            {SOCIALS.map(({ id, label, href, Icon }) => (
              <li key={id}>
                <a
                  href={href}
                  target={href.startsWith("mailto:") ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  data-testid={`hero-social-${id}`}
                  aria-label={label}
                  className="group inline-flex h-10 w-10 items-center justify-center rounded-sm border border-line text-ink-secondary transition hover:border-accent/60 hover:bg-accent/5 hover:text-accent hover:shadow-[0_0_18px_rgba(0,229,255,0.18)]"
                >
                  <Icon size={16} strokeWidth={1.6} />
                </a>
              </li>
            ))}
            <li className="ml-3 text-ink-dim">// secure_channels</li>
          </ul>
        </div>
      </div>

      {/* Bottom HUD strip */}
      <div className="mx-auto max-w-6xl border-t border-line px-6 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">
          <span>SYS::ONLINE</span>
          <span>UPLINK::STABLE</span>
          <span>REGION::IN — REMOTE_FRIENDLY</span>
          <span>ENC::TLS-1.3</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
